import React, { useMemo, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, SelectPicker, Toggle,
} from 'rsuite';
import { ItemDataType } from 'rsuite/cjs/internals/types';
import axios from 'axios';
import {
  AllegroReturnPolicy,
  AllegroReturnPolicyAvailabilityRange,
  AllegroReturnPolicyReturnCostCoveredBy,
} from '../../../../../api/allegro/returnpolicy/returnPolicyTypes';
import {
  createAllegroReturnPolicy,
  updateAllegroReturnPolicy,
} from '../../../../../api/allegro/returnpolicy/returnPolicy';
import { showErrorMessage, showSuccessMessage } from '../../../../../utils/message';
import { ALL_COUNTRY_CODES } from '../../../../../utils/countries';
import Textarea from '../../../../Textarea';
import { Edit, getInitialState, reducer } from './returnPolicyState';
import validationModel from './returnPolicyValidation';
import mapRequest from './returnPolicyRequest';
import { getRestrictionCauses, requiresPolicyDetails, requiresRestrictionCause } from './returnPolicyRules';

const COUNTRY_CODES: ItemDataType[] = ALL_COUNTRY_CODES.map((countryCode) => ({
  value: countryCode,
  label: countryCode,
}));

const AVAILABILITY_RANGES: ItemDataType[] = Object
  .values(AllegroReturnPolicyAvailabilityRange)
  .map((value) => ({ value, label: value }));

const COVERED_BY_OPTIONS: ItemDataType[] = Object
  .values(AllegroReturnPolicyReturnCostCoveredBy)
  .map((value) => ({ value, label: value }));

const WITHDRAWAL_PERIOD_OPTIONS: ItemDataType[] = Array.from(
  { length: 365 - 14 + 1 },
  (_, index) => {
    const days = index + 14;
    return { value: `P${days}D`, label: `${days} days` };
  },
);

function getRestrictionCausesData(
  range: AllegroReturnPolicyAvailabilityRange | null,
): ItemDataType[] {
  return getRestrictionCauses(range)
    .map((value) => ({ value, label: value }));
}

interface Props {
  marketplaceCode: string;
  returnPolicy?: AllegroReturnPolicy;
  onCancel: () => void;
  onSaved: () => void;
}

export default function EshopSetupAllegroReturnPolicyEdit({
  marketplaceCode,
  returnPolicy,
  onCancel,
  onSaved,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, returnPolicy, getInitialState);
  const formRef = useRef<FormInstance>(null);
  const showRestrictionCause = requiresRestrictionCause(state.edit.range);
  const showPolicyDetails = requiresPolicyDetails(state.edit.range);
  const restrictionCausesData = useMemo(
    () => getRestrictionCausesData(state.edit.range),
    [state.edit.range],
  );

  async function saveReturnPolicy() {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      const request = mapRequest(state.edit);

      if (returnPolicy) {
        await updateAllegroReturnPolicy(marketplaceCode, returnPolicy.id, request);
      } else {
        await createAllegroReturnPolicy(marketplaceCode, request);
      }

      showSuccessMessage('Return terms successfully saved.');
      onSaved();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSave() {
    if (!formRef.current?.check()) {
      return;
    }

    saveReturnPolicy().then();
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      model={validationModel}
      layout="vertical"
    >
      <Form.Group>
        <Form.ControlLabel>Label</Form.ControlLabel>
        <Form.Control name="label" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Availability range</Form.ControlLabel>
        <Form.Control
          name="range"
          accepter={SelectPicker}
          data={AVAILABILITY_RANGES}
          cleanable={false}
          searchable={false}
          block
        />
      </Form.Group>
      {showRestrictionCause && (
        <>
          <Form.Group>
            <Form.ControlLabel>Restriction cause</Form.ControlLabel>
            <Form.Control
              name="restrictionCauseName"
              accepter={SelectPicker}
              data={restrictionCausesData}
              cleanable
              searchable
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Restriction cause description</Form.ControlLabel>
            <Form.Control
              name="restrictionCauseDescription"
              accepter={Textarea}
            />
          </Form.Group>
        </>
      )}
      {showPolicyDetails && (
        <>
          <Form.Group>
            <Form.ControlLabel>Withdrawal period</Form.ControlLabel>
            <Form.Control
              name="withdrawalPeriod"
              accepter={SelectPicker}
              data={WITHDRAWAL_PERIOD_OPTIONS}
              searchable
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Return cost covered by</Form.ControlLabel>
            <Form.Control
              name="coveredBy"
              accepter={SelectPicker}
              data={COVERED_BY_OPTIONS}
              cleanable={false}
              searchable={false}
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Name</Form.ControlLabel>
            <Form.Control name="name" type="text" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Street</Form.ControlLabel>
            <Form.Control name="street" type="text" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Postal code</Form.ControlLabel>
            <Form.Control name="postCode" type="text" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>City</Form.ControlLabel>
            <Form.Control name="city" type="text" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Country code</Form.ControlLabel>
            <Form.Control
              name="countryCode"
              accepter={SelectPicker}
              data={COUNTRY_CODES}
              cleanable={false}
              searchable
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Email</Form.ControlLabel>
            <Form.Control name="email" type="email" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Phone number</Form.ControlLabel>
            <Form.Control name="phoneNumber" type="tel" />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Cash on delivery not allowed</Form.ControlLabel>
            <Form.Control
              name="cashOnDeliveryNotAllowed"
              checked={state.edit.cashOnDeliveryNotAllowed}
              accepter={Toggle}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Free accessories return required</Form.ControlLabel>
            <Form.Control
              name="freeAccessoriesReturnRequired"
              checked={state.edit.freeAccessoriesReturnRequired}
              accepter={Toggle}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Refund lowered by received discount</Form.ControlLabel>
            <Form.Control
              name="refundLoweredByReceivedDiscount"
              checked={state.edit.refundLoweredByReceivedDiscount}
              accepter={Toggle}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Business return allowed</Form.ControlLabel>
            <Form.Control
              name="businessReturnAllowed"
              checked={state.edit.businessReturnAllowed}
              accepter={Toggle}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Collect by seller only</Form.ControlLabel>
            <Form.Control
              name="collectBySellerOnly"
              checked={state.edit.collectBySellerOnly}
              accepter={Toggle}
            />
          </Form.Group>
        </>
      )}
      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSave()}
          loading={state.status === 'saving'}
          type="submit"
        >
          Save
        </Button>
        <Button appearance="ghost" onClick={() => onCancel()}>
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}

EshopSetupAllegroReturnPolicyEdit.defaultProps = {
  returnPolicy: undefined,
};
